import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import {
    ChevronLeft,
    ChevronRight,
    Heart,
    MessageCircle,
    Monitor,
    Pencil,
    Share2,
    Smartphone,
    Star,
    X,
} from 'lucide-react';
import { getTaskTitle, guessPlatformByTask } from './historyUtils';
import { FormatOutlineView, FormatCopywritingView } from '../../components/shared/ContentFormatters';

const TaskPreviewModal = ({ task, pages, open, onClose }) => {
    const [deviceMode, setDeviceMode] = useState('mobile');
    const [mobileImgIdx, setMobileImgIdx] = useState(0);
    const [webImgIdx, setWebImgIdx] = useState(0);

    useEffect(() => {
        if (open) {
            setDeviceMode('mobile');
            setMobileImgIdx(0);
            setWebImgIdx(0);
        }
    }, [open, task?.task_id]);

    if (!open || !task) return null;

    const platform = guessPlatformByTask(task);
    const title = getTaskTitle(task);
    const textBody = String(task.copywriting || pages.map((p) => p.content).filter(Boolean).join('\n\n')).trim();
    const isDraft = task.status === 'draft' && !task.copywriting;
    const outlineText = pages.map((p) => p.content).filter(Boolean).join('\n\n');
    let tags = Array.isArray(task.tags) ? task.tags.map((tag) => String(tag).replace(/^#/, '')) : [];

    // 如果 task.tags 为空，尝试从正文 textBody 中提取带有 # 的标签作为兜底机制
    if (tags.length === 0 && textBody) {
        const extractedTags = textBody.match(/#[^\s#]+/g);
        if (extractedTags) {
            tags = extractedTags.map(tag => tag.substring(1));
        }
    }

    const images = pages.map((p) => p.image?.url).filter(Boolean);
    const cover = images[0] || '';
    const lines = textBody.split('\n').map((line) => line.trim()).filter(Boolean);

    const renderXiaohongshuMobile = () => (
        <div
            className="relative mx-auto overflow-hidden rounded-[44px] bg-white shadow-2xl ring-1 ring-gray-900/10"
            style={{ width: 390, height: 844 }}
        >
            {/* 灵动岛 */}
            <div className="absolute left-1/2 top-2 z-20 h-[30px] w-[120px] -translate-x-1/2 rounded-full bg-black" />

            {/* 状态栏 */}
            <div className="relative z-10 flex h-[54px] shrink-0 items-end justify-between bg-white px-7 pb-1">
                <span className="text-[12px] font-semibold text-gray-900">19:21</span>
                <div className="flex items-center gap-1">
                    <svg className="h-3.5 w-3.5 text-gray-900" viewBox="0 0 24 24" fill="currentColor"><path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3c-1.65-1.66-4.34-1.66-6 0zm-4-4l2 2c2.76-2.76 7.24-2.76 10 0l2-2C15.14 9.14 8.87 9.14 5 13z" /></svg>
                    <svg className="h-3.5 w-3.5 text-gray-900" viewBox="0 0 24 24" fill="currentColor"><path d="M2 22h20V2z M17 7L2 22h15z" /></svg>
                    <div className="ml-0.5 flex h-[11px] w-[22px] items-center rounded-sm border border-gray-900 px-[1px]"><div className="h-[7px] w-[14px] rounded-[1px] bg-gray-900" /></div>
                </div>
            </div>

            {/* XHS 顶部导航栏 */}
            <div className="flex shrink-0 items-center justify-between bg-white px-3 py-1.5">
                <div className="flex items-center gap-2">
                    <ChevronLeft className="h-5 w-5 text-gray-800" />
                    <div className="h-8 w-8 rounded-full bg-gradient-to-br from-purple-400 to-pink-400" />
                    <span className="text-[13px] font-semibold text-gray-900">{task.skill_name || 'ContentOS'}</span>
                </div>
                <div className="flex items-center gap-3">
                    <button type="button" className="rounded-full border border-[#ff2442] px-3.5 py-1 text-xs font-bold text-[#ff2442]">关注</button>
                    <Share2 className="h-5 w-5 text-gray-800" />
                </div>
            </div>

            {/* 可滚动内容 */}
            <div className="overflow-y-auto no-scrollbar bg-white" style={{ height: 'calc(100% - 54px - 44px - 54px)' }}>
                {/* 图片 */}
                <div className="relative">
                    {images.length > 0 ? (
                        <img src={images[mobileImgIdx] || cover} alt={title} className="w-full" />
                    ) : (
                        <div className="flex aspect-square items-center justify-center text-sm text-gray-400">暂无封面图</div>
                    )}
                </div>
                {/* 图片指示器 */}
                {images.length > 1 && (
                    <div className="flex items-center justify-center gap-1.5 py-2">
                        {images.map((_, i) => (
                            <button
                                key={`dot_m_${i}`}
                                type="button"
                                onClick={() => setMobileImgIdx(i)}
                                className={`rounded-full transition-all ${i === mobileImgIdx ? 'h-[7px] w-[7px] bg-[#ff2442]' : 'h-1.5 w-1.5 bg-gray-300'}`}
                            />
                        ))}
                    </div>
                )}

                {/* 正文 */}
                <div className="space-y-2 bg-white px-4 pb-24 pt-2">
                    <h3 className="text-[15px] font-bold leading-snug text-gray-900">{title}</h3>
                    <div className="text-[13px] leading-[22px] text-gray-700">
                        {isDraft
                            ? <FormatOutlineView text={outlineText} />
                            : <FormatCopywritingView text={textBody} fontSize="13px" />
                        }
                    </div>
                    {tags.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 pt-2">
                            {tags.map((tag) => (
                                <span key={tag} className="text-[14px] font-medium text-[#13386C]">#{tag}&nbsp;</span>
                            ))}
                        </div>
                    )}
                    <div className="pt-2 text-[11px] text-gray-400">刚刚 · ContentOS预览</div>
                    <div className="border-t border-gray-100 mt-2 pt-3">
                        <div className="text-[11px] text-gray-400">共 0 条评论</div>
                    </div>
                </div>
            </div>

            {/* 底部互动栏 */}
            <div className="absolute bottom-[34px] left-0 right-0 flex items-center gap-2 border-t border-gray-100 bg-white px-3 py-2">
                <Pencil className="h-4 w-4 shrink-0 text-gray-400" />
                <div className="flex-1 rounded-full bg-gray-100 px-3 py-1.5 text-[11px] text-gray-400">说点什么...</div>
                <div className="flex items-center gap-4 pl-1">
                    <div className="flex items-center gap-0.5 text-gray-500">
                        <Heart className="h-[18px] w-[18px]" /><span className="text-[11px]">0</span>
                    </div>
                    <div className="flex items-center gap-0.5 text-gray-500">
                        <Star className="h-[18px] w-[18px]" /><span className="text-[11px]">0</span>
                    </div>
                    <div className="flex items-center gap-0.5 text-gray-500">
                        <MessageCircle className="h-[18px] w-[18px]" /><span className="text-[11px]">0</span>
                    </div>
                </div>
            </div>

            {/* Home Indicator */}
            <div className="absolute bottom-0 left-0 right-0 flex h-[34px] items-center justify-center bg-white">
                <span className="h-[5px] w-[134px] rounded-full bg-gray-900/20" />
            </div>
        </div>
    );

    const renderXiaohongshuWeb = () => (
        <div className="mx-auto flex w-full max-w-5xl overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl" style={{ maxHeight: '82vh' }}>
            {/* 左侧：图片 - 浅色背景，自然比例 */}
            <div className="relative flex w-1/2 shrink-0 items-center justify-center bg-[#f5f5f4]">
                {images.length > 0 ? (
                    <img
                        src={images[webImgIdx] || cover}
                        alt={title}
                        className="h-full w-full object-contain"
                    />
                ) : (
                    <div className="flex h-full w-full items-center justify-center text-sm text-gray-400">暂无封面图</div>
                )}
                {/* 左右切换箭头 */}
                {images.length > 1 && webImgIdx > 0 && (
                    <button type="button" onClick={() => setWebImgIdx(i => i - 1)} className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-black/30 p-1.5 text-white transition-all hover:bg-black/50">
                        <ChevronLeft className="h-5 w-5" />
                    </button>
                )}
                {images.length > 1 && webImgIdx < images.length - 1 && (
                    <button type="button" onClick={() => setWebImgIdx(i => i + 1)} className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-black/30 p-1.5 text-white transition-all hover:bg-black/50">
                        <ChevronRight className="h-5 w-5" />
                    </button>
                )}
                {/* 指示器 */}
                {images.length > 1 && (
                    <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-1.5">
                        {images.map((_, i) => (
                            <button
                                key={`dot_w_${i}`}
                                type="button"
                                onClick={() => setWebImgIdx(i)}
                                className={`rounded-full transition-all ${i === webImgIdx ? 'h-2 w-2 bg-white shadow' : 'h-1.5 w-1.5 bg-white/50'}`}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* 右侧：内容 - 独立滚动 */}
            <div className="flex min-w-0 flex-1 flex-col border-l border-gray-100">
                {/* 作者栏 */}
                <div className="flex items-center justify-between px-5 py-3">
                    <div className="flex items-center gap-2.5">
                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-purple-400 to-pink-400" />
                        <div className="text-sm font-semibold text-gray-900">{task.skill_name || 'ContentOS'}</div>
                    </div>
                    <button type="button" className="rounded-full bg-[#ff2442] px-5 py-1.5 text-xs font-bold text-white">关注</button>
                </div>

                {/* 可滚动内容区 */}
                <div className="flex-1 overflow-y-auto px-5 pb-4">
                    <div>
                        {/* 标题 */}
                        <h3 className="text-[17px] font-bold leading-7 text-gray-900">{title}</h3>
                        {/* 正文 */}
                        <div className="mt-2 text-[14px] leading-7 text-gray-700">
                            {isDraft
                                ? <FormatOutlineView text={outlineText} />
                                : <FormatCopywritingView text={textBody} />
                            }
                        </div>
                        {/* 标签 */}
                        {tags.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-x-1 gap-y-1">
                                {tags.map((tag) => (
                                    <span key={tag} className="text-[14px] font-medium text-[#2f7df6]">#{tag}&nbsp;</span>
                                ))}
                            </div>
                        )}
                        {/* 时间 */}
                        <div className="mt-3 text-xs text-gray-400">刚刚 · ContentOS预览</div>
                    </div>

                    {/* 评论区 */}
                    <div className="mt-4 border-t border-gray-100 pt-3">
                        <div className="text-sm text-gray-500">共 0 条评论</div>
                    </div>
                </div>

                {/* 底部互动栏 */}
                <div className="flex items-center gap-3 border-t border-gray-100 px-5 py-2.5">
                    <div className="text-gray-400">😊</div>
                    <div className="flex-1 rounded-full bg-gray-100 px-4 py-2 text-xs text-gray-400">说点什么...</div>
                    <div className="flex items-center gap-5">
                        <div className="flex items-center gap-1 text-gray-400 transition-colors hover:text-[#ff2442]">
                            <Heart className="h-[20px] w-[20px]" /><span className="text-xs font-medium">0</span>
                        </div>
                        <div className="flex items-center gap-1 text-gray-400 transition-colors hover:text-yellow-500">
                            <Star className="h-[20px] w-[20px]" /><span className="text-xs font-medium">0</span>
                        </div>
                        <div className="flex items-center gap-1 text-gray-400 transition-colors hover:text-gray-600">
                            <MessageCircle className="h-[20px] w-[20px]" /><span className="text-xs font-medium">0</span>
                        </div>
                        <div className="text-gray-400 transition-colors hover:text-gray-600">
                            <Share2 className="h-[20px] w-[20px]" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderWechatMobile = () => (
        <div className="mx-auto w-[360px] rounded-[36px] border-[10px] border-black bg-white shadow-2xl">
            <div className="flex h-7 items-center justify-center bg-black">
                <span className="h-1.5 w-20 rounded-full bg-white/75" />
            </div>
            <div className="bg-[#07c160] px-4 py-3 text-center text-sm font-semibold text-white">公众号</div>
            <div className="max-h-[70vh] overflow-y-auto p-5">
                <h3 className="text-[22px] font-semibold leading-9 text-gray-900">{title}</h3>
                <div className="mt-2 text-xs text-gray-500">ContentOS · 预览</div>
                {cover && (
                    <div className="mt-4 overflow-hidden rounded-xl">
                        <img src={cover} alt={title} className="w-full object-cover" />
                    </div>
                )}
                <div className="mt-4 space-y-4 text-[15px] leading-8 text-gray-700">
                    {lines.length > 0 ? lines.map((line, index) => <p key={`${line}_${index}`}>{line}</p>) : <p>暂无正文内容</p>}
                </div>
                {tags.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                        {tags.map((tag) => (
                            <span key={tag} className="text-[14px] text-[#576b95]">#{tag}</span>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );

    const renderWechatWeb = () => (
        <div className="mx-auto w-full max-w-4xl rounded-3xl border border-gray-200 bg-white px-10 py-9 shadow-xl">
            <div className="mx-auto max-w-3xl">
                <div className="mb-5 text-sm font-medium text-[#07c160]">公众号 Web 预览</div>
                <h3 className="text-3xl font-semibold leading-[46px] text-gray-900">{title}</h3>
                <div className="mt-3 text-sm text-gray-500">ContentOS · 今日发布</div>
                {cover && (
                    <div className="mt-6 overflow-hidden rounded-2xl">
                        <img src={cover} alt={title} className="w-full object-cover" />
                    </div>
                )}
                <div className="mt-8 space-y-5 text-[17px] leading-9 text-gray-700">
                    {lines.length > 0 ? lines.map((line, index) => <p key={`${line}_${index}`}>{line}</p>) : <p>暂无正文内容</p>}
                </div>
                {tags.length > 0 && (
                    <div className="mt-6 flex flex-wrap gap-2">
                        {tags.map((tag) => (
                            <span key={tag} className="text-[16px] text-[#576b95]">#{tag}</span>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );

    const renderPreviewContent = () => {
        if (platform === 'wechat') {
            return deviceMode === 'mobile' ? renderWechatMobile() : renderWechatWeb();
        }
        return deviceMode === 'mobile' ? renderXiaohongshuMobile() : renderXiaohongshuWeb();
    };

    return ReactDOM.createPortal(
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 md:p-8">
            <div className="absolute inset-0 bg-gray-900/70 backdrop-blur-sm" onClick={onClose} />
            <div className="relative flex h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-3xl border border-gray-200 bg-[#F8FAFC] shadow-2xl">
                <div className="flex items-center justify-between border-b border-gray-200 bg-white px-5 py-4">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">媒体预览</h3>
                        <p className="mt-0.5 text-xs text-gray-500">当前媒体：{platform === 'wechat' ? '公众号' : '小红书'}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => setDeviceMode('mobile')}
                            className={`inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${deviceMode === 'mobile' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                        >
                            <Smartphone className="h-4 w-4" />
                            手机端
                        </button>
                        <button
                            type="button"
                            onClick={() => setDeviceMode('web')}
                            className={`inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${deviceMode === 'web' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                        >
                            <Monitor className="h-4 w-4" />
                            Web端
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto p-5 md:p-8">
                    {renderPreviewContent()}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default TaskPreviewModal;
